import React from 'react';
import { SCHOOL_EMAIL, SCHOOL_FORM_LABEL, type ReviseSchoolCopy } from './content';

/**
 * The school licence, under the pricing on both boards.
 *
 * It answers the same question the tiers above it do — what does this cost —
 * for the other kind of buyer, which is why it lives inside the pricing
 * section rather than becoming a section of its own.
 *
 * The two boards price it differently and the shapes are not
 * interchangeable: the NSC is banded on school fees, the IEB is one flat
 * price. The panel renders whichever the copy carries (`bands` or `flat`)
 * and never both.
 *
 * The one place on this site that publishes an email address. That is not an
 * oversight: a licence is arranged with a person rather than bought on the
 * platform, so a page describing one has to say how to start. Everything a
 * learner can buy alone is a button.
 */
export const ReviseSchoolLicence: React.FC<{ copy: ReviseSchoolCopy }> = ({ copy }) => (
  <div className="rv-school">
    <div className="rv-school-cols">
      <div>
        <p className="rv-kicker">{copy.coversEyebrow}</p>
        <p className="rv-p">{copy.covers}</p>
      </div>
      <div>
        <p className="rv-kicker">{copy.selfEyebrow}</p>
        <p className="rv-p">{copy.self}</p>
      </div>
    </div>

    <div className="rv-school-panel">
      <div className="rv-school-head">
        <h3>{copy.title}</h3>
        <span className="rv-school-badge">{copy.badge}</span>
      </div>

      {copy.lede && <p className="rv-school-lede">{copy.lede}</p>}

      {copy.bands && (
        <div className="rv-school-bands">
          {copy.bandsHead && (
            <div className="rv-school-bandhead">
              <span>{copy.bandsHead.left}</span>
              <span>{copy.bandsHead.right}</span>
            </div>
          )}
          {copy.bands.map((b) => (
            <div className="rv-school-band" key={b.label}>
              <span>{b.label}</span>
              <b>{b.price}</b>
            </div>
          ))}
        </div>
      )}

      {copy.flat && (
        <div className="rv-school-flat">
          <span className="rv-school-flatprice">{copy.flat.price}</span>
          <p className="rv-p">{copy.flat.body}</p>
        </div>
      )}

      {copy.note && <p className="rv-school-note">{copy.note}</p>}
    </div>

    {/* A form when there is one, the address when there is not. The form is
        preferred: it asks the school for what is needed to quote, and it
        keeps the only email address off a public page. */}
    {copy.formUrl ? (
      <div className="rv-ctas rv-school-cta">
        <a
          className="rv-btn rv-btn-primary"
          href={copy.formUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {SCHOOL_FORM_LABEL} &rarr;
        </a>
      </div>
    ) : (
      <p className="rv-school-contact">
        {copy.contact} <a href={`mailto:${SCHOOL_EMAIL}`}>{SCHOOL_EMAIL}</a>
      </p>
    )}
  </div>
);
